import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import DocumentUpload from "@/components/DocumentUpload";
import CityAutocomplete from "@/components/CityAutocomplete";

const URGENCIES = [
  { v: "faible", l: "Faible — pas pressé" },
  { v: "moyenne", l: "Moyenne — quelques jours" },
  { v: "haute", l: "Haute — sous 48h" },
  { v: "critique", l: "Critique — immédiat" },
];

const SERVICES = [
  { v: "consultation_ecrite", l: "Consultation écrite" },
  { v: "appel", l: "Rendez-vous téléphonique" },
  { v: "visio", l: "Visioconférence" },
  { v: "mission_complete", l: "Mission complète" },
  { v: "devis", l: "Devis personnalisé" },
];

export default function MissionNew() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preselected = params.get("expert");
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    title: "", category: "", subcategory: "", description: "",
    urgency: "moyenne", city: "", budget: "", service_type: "consultation_ecrite",
    expert_id: preselected || null,
  });
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { apiClient.get("/categories").then(r => setCats(r.data)); }, []);

  const runAI = async () => {
    if (!form.description || form.description.length < 30) {
      toast.error("Veuillez décrire votre situation plus en détail (au moins 30 caractères).");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await apiClient.post("/ai/summarize-mission", { text: form.description });
      setAiResult(data);
      if (data.category && cats.find(c => c.id === data.category)) {
        set("category", data.category);
      }
      if (data.urgency_level) set("urgency", data.urgency_level);
      toast.success("Analyse de votre demande effectuée");
    } catch (e) {
      toast.error("L'analyse n'a pu être finalisée");
    } finally {
      setAiLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!accept) { toast.error("Veuillez accepter les conditions générales d'utilisation."); return; }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/missions", form);
      // Attach previously uploaded documents to the new mission
      if (docs.length > 0) {
        await Promise.all(
          docs.map(d => apiClient.patch(`/uploads/${d.id}/attach`, { mission_id: data.id }).catch(() => null))
        );
      }
      toast.success("Demande transmise");
      nav(`/messages/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "La transmission a échoué");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <Navbar variant="light" />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-3">Demande d'accompagnement</div>
        <h1 className="font-serif text-4xl tracking-tight text-[#0B3A2A] mb-2">Exposez votre situation</h1>
        <p className="text-slate-600 mb-10">Plus votre demande sera précise, plus la sélection du spécialiste référencé sera pertinente.</p>

        <form onSubmit={onSubmit} className="space-y-6" data-testid="mission-form">
          <Card className="p-6 lg:p-8 rounded-2xl border-slate-100 space-y-5">
            <div>
              <Label className="text-[#0B3A2A] mb-2 block">Titre de la demande</Label>
              <Input required value={form.title} onChange={e => set("title", e.target.value)}
                placeholder="ex: Restructuration d'une SARL en SAS"
                data-testid="mission-title" className="h-11 rounded-xl border-slate-200" />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Domaine</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger className="h-11 rounded-xl border-slate-200" data-testid="mission-category">
                    <SelectValue placeholder="Choisir un domaine" />
                  </SelectTrigger>
                  <SelectContent>
                    {cats.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Sous-catégorie (optionnel)</Label>
                <Input value={form.subcategory} onChange={e => set("subcategory", e.target.value)} className="h-11 rounded-xl border-slate-200" />
              </div>
            </div>
            <div>
              <Label className="text-[#0B3A2A] mb-2 block">Description détaillée</Label>
              <Textarea required rows={6} value={form.description} onChange={e => set("description", e.target.value)}
                placeholder="Expliquez votre situation, le contexte, vos enjeux..."
                data-testid="mission-description" className="rounded-xl border-slate-200 resize-y" />
              <Button type="button" variant="outline" size="sm" onClick={runAI} disabled={aiLoading} data-testid="ai-summary-btn"
                className="mt-3 rounded-full border-[#C9A961]/40 text-[#0B3A2A] hover:bg-[#C9A961]/10">
                {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2 text-[#C9A961]" />}
                {aiLoading ? "Analyse en cours…" : "Analyser ma demande"}
              </Button>
            </div>
            {aiResult && (
              <Card className="p-5 rounded-xl bg-[#C9A961]/5 border-[#C9A961]/30" data-testid="ai-result">
                <div className="text-xs uppercase tracking-wider text-[#C9A961] mb-2">Synthèse de votre demande</div>
                <p className="text-sm text-[#0B3A2A] mb-3">{aiResult.summary}</p>
                {aiResult.key_points?.length > 0 && (
                  <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
                    {aiResult.key_points.map((k) => <li key={`kp-${k}`}>{k}</li>)}
                  </ul>
                )}
                {aiResult.missing_info?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-medium text-slate-500 mb-1">Informations manquantes :</div>
                    <ul className="text-sm text-slate-600 list-disc pl-5">{aiResult.missing_info.map((k) => <li key={`mi-${k}`}>{k}</li>)}</ul>
                  </div>
                )}
              </Card>
            )}
          </Card>

          <Card className="p-6 lg:p-8 rounded-2xl border-slate-100">
            <DocumentUpload
              initialDocs={docs}
              onDocsChange={setDocs}
              title="Pièces justificatives"
              description="Joignez les documents utiles (contrats, statuts, bilans, courriers reçus…). Ces pièces resteront strictement confidentielles et ne seront accessibles qu'au spécialiste assigné une fois le dossier engagé."
            />
          </Card>

          <Card className="p-6 lg:p-8 rounded-2xl border-slate-100 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Urgence</Label>
                <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
                  <SelectTrigger className="h-11 rounded-xl border-slate-200" data-testid="mission-urgency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>{URGENCIES.map(u => <SelectItem key={u.v} value={u.v}>{u.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Type de prestation</Label>
                <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                  <SelectTrigger className="h-11 rounded-xl border-slate-200" data-testid="mission-service">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>{SERVICES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Ville / zone géographique</Label>
                <CityAutocomplete
                  value={form.city}
                  onChange={(name) => set("city", name)}
                  placeholder="Ex : Paris, Lyon, Bordeaux…"
                  inputClassName="h-11 rounded-xl border-slate-200 bg-white"
                  data-testid="mission-city"
                />
              </div>
              <div>
                <Label className="text-[#0B3A2A] mb-2 block">Honoraires indicatifs</Label>
                <Input value={form.budget} onChange={e => set("budget", e.target.value)} placeholder="ex : 500–1 500 €" className="h-11 rounded-xl border-slate-200" />
              </div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={accept} onCheckedChange={setAccept} data-testid="accept-cgu" className="mt-1" />
              <span className="text-sm text-slate-600">J'accepte les conditions générales d'utilisation et confirme la confidentialité de ma demande. CONFILEX agit en qualité d'intermédiaire technique de mise en relation et n'exerce aucune activité de conseil juridique, fiscal ou comptable.</span>
            </label>
          </Card>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={() => nav(-1)}>Annuler</Button>
            <Button type="submit" disabled={loading} data-testid="mission-submit"
              className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold px-8 h-11">
              {loading ? "Transmission…" : "Soumettre ma demande"}
            </Button>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}
